import { getAsyncContextStrategy } from '../asyncContext/index.js';
import { getMainCarrier } from '../carrier.js';
import { getClient, getCurrentScope } from '../currentScopes.js';
import { isEnabled } from '../exports.js';
import { debug } from './debug-logger.js';
import { getActiveSpan, spanToTraceHeader } from './spanUtils.js';
import { getDynamicSamplingContextFromSpan, getDynamicSamplingContextFromScope } from '../tracing/dynamicSamplingContext.js';
import { TRACEPARENT_REGEXP, generateSentryTraceHeader } from './tracing.js';
import { dynamicSamplingContextToSentryBaggageHeader } from './baggage.js';

/**
 * Extracts trace propagation data from the current span or from the client's scope (via transaction or propagation
 * context) and serializes it to `sentry-trace` and `baggage` values to strings. These values can be used to propagate
 * a trace via our tracing Http headers or Html `<meta>` tags.
 *
 * This function also applies some validation to the generated sentry-trace and baggage values to ensure that
 * only valid strings are returned.
 *
 * @returns an object with the tracing data values. The object keys are the name of the tracing key to be used as header
 * or meta tag name.
 */
function getTraceData() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var client = options.client || getClient();
  if (!isEnabled() || !client) {
    return {};
  }
  var carrier = getMainCarrier();
  var acs = getAsyncContextStrategy(carrier);
  if (acs.getTraceData) {
    return acs.getTraceData(options);
  }
  var scope = options.scope || getCurrentScope();
  var span = options.span || getActiveSpan();
  var sentryTrace = span ? spanToTraceHeader(span) : scopeToTraceHeader(scope);
  var dsc = span ? getDynamicSamplingContextFromSpan(span) : getDynamicSamplingContextFromScope(client, scope);
  var baggage = dynamicSamplingContextToSentryBaggageHeader(dsc);
  var isValidSentryTraceHeader = TRACEPARENT_REGEXP.test(sentryTrace);
  if (!isValidSentryTraceHeader) {
    debug.warn('Invalid sentry-trace data. Cannot generate trace data');
    return {};
  }
  return {
    'sentry-trace': sentryTrace,
    baggage: baggage
  };
}

/**
 * Get a sentry-trace header value for the given scope.
 */
function scopeToTraceHeader(scope) {
  var _scope$getPropagation = scope.getPropagationContext(),
    traceId = _scope$getPropagation.traceId,
    sampled = _scope$getPropagation.sampled,
    propagationSpanId = _scope$getPropagation.propagationSpanId;
  return generateSentryTraceHeader(traceId, propagationSpanId, sampled);
}
export { getTraceData };